import { useState } from 'react';
import { ArrowRight, Building2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Modal } from '../../../components/ui/Modal';
import { EmptyState, ErrorState, QueryState } from '../../../components/ui/States';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import { Table, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { useNameLeadCompany, useUnknownLeadCompanies } from '../../../hooks/useOutreach';
import type { LeadCompanyCoverage, UnknownLeadCompany } from '../../../api/outreach';
import { plural } from '../../ProjectManagement/formatters';

const inputClass =
  'w-full rounded-md border border-border-light bg-surface-primary px-2.5 py-1 text-body-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-accent';

function UnknownRow({ person }: { person: UnknownLeadCompany }) {
  const [companyName, setCompanyName] = useState('');
  const save = useNameLeadCompany();
  const result = save.data;

  return (
    <Tr>
      <Td className="pr-3">
        <div className="font-medium text-text-primary">
          {person.linkedinUrl ? (
            <a href={person.linkedinUrl} target="_blank" rel="noreferrer" className="hover:underline" title="Open on LinkedIn">
              {person.fullName ?? 'Unnamed lead'}
            </a>
          ) : (
            person.fullName ?? 'Unnamed lead'
          )}
        </div>
        <div className="text-body-xs text-text-tertiary" title={person.reason ?? undefined}>
          {person.jobTitle ?? 'No job title'} · {person.campaigns.map((c) => c.name ?? c.id).join(', ')}
        </div>
      </Td>
      <Td className="w-72">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!companyName.trim()) return;
            save.mutate({ id: person.id, companyName: companyName.trim(), companyDomain: null });
          }}
        >
          <input
            className={inputClass}
            placeholder="Company name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            aria-label={`Company for ${person.fullName ?? 'this lead'}`}
          />
          <Button type="submit" size="sm" variant="outline" isLoading={save.isPending} disabled={!companyName.trim()}>
            Save
          </Button>
        </form>
        {save.error && <p className="mt-1 text-body-xs text-error">Not saved: {save.error.message}</p>}
        {result && result.writeErrors.length > 0 && (
          <p className="mt-1 text-body-xs text-warning-dark">
            Saved in the OS, but lemlist refused it: {result.writeErrors.join(' · ')}. The hourly sync tries again.
          </p>
        )}
      </Td>
    </Tr>
  );
}

function QueueNotes({ coverage }: { coverage: LeadCompanyCoverage }) {
  const notes: string[] = [];
  if (coverage.pending > 0) {
    notes.push(`${plural(coverage.pending, 'person', 'people')} not looked up yet: the hourly sync checks 150 per hour in Apollo.`);
  }
  if (notes.length === 0) return null;
  return <p className="text-body-xs text-text-tertiary">{notes.join(' ')}</p>;
}

function UnknownList({ unknown }: { unknown: UnknownLeadCompany[] }) {
  const { pageItems, ...paging } = usePagination(unknown);
  return (
    <>
      <Table minWidth={640}>
        <THead>
          <tr>
            <Th>Person · campaign</Th>
            <Th>Company</Th>
          </tr>
        </THead>
        <tbody>
          {pageItems.map((person) => (
            <UnknownRow key={person.id} person={person} />
          ))}
        </tbody>
      </Table>
      <Pagination {...paging} onPage={paging.setPage} />
    </>
  );
}

/**
 * The queue behind the Outbound action card: everyone in a live lemlist
 * campaign whose company neither lemlist, Attio nor Apollo could name. Saving a
 * company records it in the OS and writes it onto the person's lemlist leads.
 */
export function UnknownCompaniesModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const query = useUnknownLeadCompanies();
  const count = query.data?.coverage.unresolved;
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      title={count === undefined ? 'People without a company' : `${plural(count, 'person', 'people')} without a company`}
      description="Nobody could name the company for these people: not lemlist, Attio or Apollo. Type it in and it is saved here and written onto their lemlist leads."
    >
      <QueryState query={query} errorTitle="Couldn’t load the queue">
        {({ coverage, unknown }) => (
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <QueueNotes coverage={coverage} />
            {coverage.lastWriteErrors > 0 && (
              <ErrorState
                compact
                title={`lemlist refused the company for ${plural(coverage.lastWriteErrors, 'person', 'people')} on the last try`}
                error="The reason is on the lemlist integration’s latest hourly sync run."
              />
            )}
            {unknown.length === 0 ? (
              <EmptyState title="Every person in a live campaign has a company" compact />
            ) : (
              <UnknownList unknown={unknown} />
            )}
          </div>
        )}
      </QueryState>
    </Modal>
  );
}

/** The action card on Outbound: "189 people without a company → open". Hidden when the queue is empty. */
export function UnknownCompaniesCard({ onOpen }: { onOpen: () => void }) {
  const query = useUnknownLeadCompanies();
  if (query.error) {
    return <ErrorState compact title="Couldn’t load the companies to identify" error={query.error} onRetry={() => query.refetch()} />;
  }
  const unresolved = query.data?.coverage.unresolved ?? 0;
  if (unresolved === 0) return null;
  return (
    <Card padding="none">
      <button
        type="button"
        onClick={onOpen}
        className="group flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-secondary rounded-2xl transition-colors"
      >
        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-warning-light text-warning-dark">
          <Building2 className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-body-sm font-medium text-text-primary">
            {plural(unresolved, 'person', 'people')} without a company
          </span>
          <span className="block text-body-xs text-text-tertiary">
            In live campaigns, and neither lemlist, Attio nor Apollo could name their company. Fill it in here.
          </span>
        </span>
        <span className="flex items-center gap-1 text-body-sm text-text-secondary group-hover:text-text-primary">
          Open <ArrowRight className="h-4 w-4" />
        </span>
      </button>
    </Card>
  );
}
